import { ApiError } from "../api/api-error";
import type { TranslationKey } from "../i18n/translation";
import { describeServerMessage } from "./describe-server-message";

/**
 * Maps a validation response to the message shown under each field.
 *
 * `ValidationBehaviour` sets `code: failure.PropertyName` and `ErrorOrEndpointFilter` groups by
 * that code, so `ProblemDetails.errors` is keyed by the property. The same translations are used
 * by `describeApiError` for callers that show a banner instead of field messages.
 *
 * FluentValidation's default messages are shaped rather than enumerable — every `NotEmpty()`
 * produces the same sentence with a different property name in it — so they are matched by
 * pattern. The property name is discarded: the message sits under its own field, and the field's
 * label is already translated while the server's property name is not. Unknown messages still
 * mark their field with a localized validation fallback.
 */
const MESSAGE_KEYS: [RegExp, TranslationKey][] = [
  [/^Enter a Kazakhstan phone number\.$/, "validation.phone"],
  [/^Language must be one of: .+\.$/, "validation.invalid"],
  [/^'.+' must be one of: .+\.$/, "validation.invalid"],
  [
    /^'.+' must be (?:between |greater than |less than )/,
    "validation.out_of_range",
  ],
  [
    /^The length of '.+' must be between \d+ and \d+ characters\./,
    "validation.out_of_range",
  ],
  [/^Passwords must be at least \d+ characters\.$/, "validation.too_short"],
  [
    /^Passwords must have at least one non alphanumeric character\.$/,
    "validation.password_symbol",
  ],
  [
    /^Passwords must have at least one digit \('0'-'9'\)\.$/,
    "validation.password_digit",
  ],
  [
    /^Passwords must have at least one lowercase \('a'-'z'\)\.$/,
    "validation.password_lowercase",
  ],
  [
    /^Passwords must have at least one uppercase \('A'-'Z'\)\.$/,
    "validation.password_uppercase",
  ],
  [
    /^Passwords must use at least \d+ different characters\.$/,
    "validation.password_unique",
  ],
  // A hand-written message rather than a shaped one, so it is matched whole. The sign-in form
  // makes the same judgement before it posts, and this is what marks the field when a value
  // reaches the server that the mirror let through.
  [
    /^Enter an email address, a phone number or an IIN\.$/,
    "auth.sign_in.identifier_invalid",
  ],

  // Hand-written, like the sign-in one above, and matched whole. It is the only field message
  // here that answers something the customer did not type: a consultant's card switched off
  // between the page loading and the form being sent.
  [
    /^That consultant is not taking bookings\./,
    "booking.specialist_unavailable",
  ],

  // `AccountIin`'s sentence, shared by registration and the portal's IIN box.
  [/^That is not a valid IIN\.$/, "auth.register.iin_invalid"],

  [/^'.+' must not be empty\.$/, "validation.required"],
  [/^'.+' must not be null\.$/, "validation.required"],
  [/^'.+' is not a valid email address\.$/, "validation.email"],
  [
    /^The length of '.+' must be at least \d+ characters\./,
    "validation.too_short",
  ],
  [
    /^The length of '.+' must be \d+ characters or fewer\./,
    "validation.too_long",
  ],
  [/^'.+' is not in the correct format\.$/, "validation.format"],
];

export function describeFieldErrors(
  error: unknown,
): Record<string, TranslationKey> {
  if (!(error instanceof ApiError)) {
    return {};
  }

  const errors = error.problem?.errors;

  if (errors === undefined) {
    return {};
  }

  const described: Record<string, TranslationKey> = {};

  for (const [property, messages] of Object.entries(errors)) {
    if (messages.length === 0) continue;
    const key =
      MESSAGE_KEYS.find(([pattern]) => pattern.test(messages[0]))?.[1] ??
      describeServerMessage(messages[0]) ??
      "validation.invalid";

    described[property.charAt(0).toLowerCase() + property.slice(1)] = key;
  }

  return described;
}
